import { Button, Input, Textarea } from "@cloudflare/kumo";
import {
  ArrowsClockwiseIcon,
  ArrowsLeftRight,
  CheckCircle,
  Eye,
  FilmStrip,
  Flask,
  Play,
  Plus,
  SpinnerGap,
  X,
  XCircle
} from "@phosphor-icons/react";
import { type ReactElement, type ReactNode } from "react";
import { MAX_TEST_CONCURRENCY } from "@workerstation/shared";
import type { PlaygroundFiles } from "../../default-workspaces";
import type {
  TestRunFlowRecording,
  TestRunResult
} from "@workerstation/shared";

/** The exact shared response returned to Preview, HTTP, and Console. */
export type RunResult = import("../../api/execution/model.ts").PreviewResult;

export type RuntimeRunResult = RunResult;

export type RuntimeTestRunResult = TestRunResult;

export interface RuntimeError {
  message: string;
  stack?: string;
}

export type HttpMethod =
  | "GET"
  | "POST"
  | "PUT"
  | "PATCH"
  | "DELETE"
  | "HEAD"
  | "OPTIONS";

export type HeaderEntry = [string, string];

/** Request state owned by the application and edited by HttpSurface. */
export interface HttpRequestState {
  method: HttpMethod;
  headers: HeaderEntry[];
  body: string;
}

function prettyBody(body: string): string {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}

function getContentType(headers: Record<string, string>): string {
  const match = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === "content-type"
  );
  return match?.[1] ?? "text/plain";
}

function consolePrefix(level: string): string {
  if (level === "error") return "✕";
  if (level === "warn") return "!";
  return "›";
}

function consoleColor(level: string): string {
  if (level === "error") return "text-status-error";
  if (level === "warn") return "text-status-warning";
  return "text-kumo-default";
}

function statusDotColor(status: number): string {
  if (status < 300) return "bg-status-success";
  if (status < 400) return "bg-status-warning";
  return "bg-status-error";
}

function testFilesFrom(files: PlaygroundFiles): string[] {
  const testFile =
    /(?:^|\/)(?:[^/]+\.)?(?:test|spec)\.(?:[cm]?[jt]sx?)$/i;
  return Object.keys(files).filter((file) => testFile.test(file)).sort();
}

function ToggleIconButton({
  active,
  title,
  onClick,
  icon
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  icon: ReactElement;
}) {
  return (
    <Button
      variant="ghost"
      shape="square"
      aria-pressed={active}
      aria-label={title}
      title={title}
      onClick={onClick}
      icon={icon}
      className={`h-6 w-6 ${
        active
          ? "bg-kumo-fill text-kumo-default"
          : "text-kumo-subtle hover:text-kumo-default"
      }`}
    />
  );
}

function WorkerUrlInput({
  base,
  path,
  ariaLabel,
  onPathChange
}: {
  base: string;
  path: string;
  ariaLabel: string;
  onPathChange: (next: string) => void;
}) {
  return (
    <div className="flex h-8 min-w-0 flex-1 items-center overflow-hidden rounded-lg border border-kumo-line bg-kumo-base font-mono text-xs">
      <span
        className="min-w-0 shrink-[2] truncate whitespace-nowrap pl-2 text-kumo-subtle"
        title={base + path}
      >
        {base}
      </span>
      <input
        aria-label={ariaLabel}
        autoComplete="off"
        spellCheck={false}
        value={path}
        onChange={(event) => {
          const next = event.target.value;
          onPathChange(next.startsWith("/") ? next : `/${next}`);
        }}
        className="h-full w-full min-w-20 flex-1 bg-transparent pr-2 text-kumo-default outline-none"
      />
    </div>
  );
}

export interface PreviewSurfaceProps {
  result: RunResult | null;
  error: RuntimeError | null;
  path: string;
  workerUrlBase: string;
  runId: number;
  running: boolean;
  onPathChange: (path: string) => void;
  onRun: () => void;
}

/** Preview's URL bar and mounted worker response surface. */
export function PreviewSurface({
  result,
  error,
  path,
  workerUrlBase,
  runId,
  running,
  onPathChange,
  onRun
}: PreviewSurfaceProps) {
  const previewUrl = `${workerUrlBase}${path.startsWith("/") ? path : `/${path}`}`;
  const isHtmlResponse = result
    ? getContentType(result.response.headers).includes("text/html")
    : false;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <form
        className="flex shrink-0 items-center gap-2 border-b border-kumo-line p-2"
        onSubmit={(event) => {
          event.preventDefault();
          onRun();
        }}
      >
        <WorkerUrlInput
          base={workerUrlBase}
          path={path}
          ariaLabel="Preview URL"
          onPathChange={onPathChange}
        />
        <Button
          variant="secondary"
          shape="square"
          type="submit"
          loading={running}
          aria-label="Run preview"
          title="Run preview"
          icon={<Play size={16} weight="fill" />}
        />
      </form>

      <div className="relative min-h-0 flex-1">
        {result && !result.workerError ? (
          isHtmlResponse ? (
            <iframe
              key={runId}
              src={previewUrl}
              title="Worker preview"
              // Navigate to the real mounted Worker instead of a srcdoc copy so
              // modules, cookies, WebSockets, and sibling routes behave exactly
              // like the deployed page.
              sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
              className="h-full w-full border-0 bg-white"
            />
          ) : (
            <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-words bg-white p-3 font-mono text-xs text-neutral-900 dark:bg-neutral-950 dark:text-neutral-100">
              {prettyBody(result.response.body)}
            </pre>
          )
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-kumo-subtle">
            {result?.workerError || error ? (
              <pre className="max-h-full max-w-full overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-xs text-status-error">
                {result?.workerError?.message ?? error?.message}
              </pre>
            ) : (
              <>
                <Play size={40} />
                <p className="text-sm">Run your Worker to see the preview</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export interface HttpSurfaceProps {
  result: RunResult | null;
  error: RuntimeError | null;
  path: string;
  workerUrlBase: string;
  running: boolean;
  request: HttpRequestState;
  onPathChange: (path: string) => void;
  onRequestChange: (request: HttpRequestState) => void;
  onSend: () => void;
}

/** HTTP request composer and response surface. Request execution stays in App. */
export function HttpSurface({
  result,
  error,
  path,
  workerUrlBase,
  running,
  request,
  onPathChange,
  onRequestChange,
  onSend
}: HttpSurfaceProps) {
  const hasRequestBody = request.method !== "GET" && request.method !== "HEAD";

  function updateHeader(index: number, entry: HeaderEntry) {
    onRequestChange({
      ...request,
      headers: request.headers.map((current, currentIndex) =>
        currentIndex === index ? entry : current
      )
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <form
        className="flex shrink-0 items-center gap-2 border-b border-kumo-line p-2"
        onSubmit={(event) => {
          event.preventDefault();
          onSend();
        }}
      >
        <select
          aria-label="HTTP method"
          value={request.method}
          onChange={(event) =>
            onRequestChange({
              ...request,
              method: event.target.value as HttpMethod
            })
          }
          className="h-8 shrink-0 cursor-pointer rounded-md border border-kumo-line bg-kumo-base px-2 font-mono text-xs text-kumo-default"
        >
          {[
            "GET",
            "POST",
            "PUT",
            "PATCH",
            "DELETE",
            "HEAD",
            "OPTIONS"
          ].map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
        <WorkerUrlInput
          base={workerUrlBase}
          path={path}
          ariaLabel="Request URL"
          onPathChange={onPathChange}
        />
        <Button
          variant="secondary"
          shape="square"
          type="submit"
          loading={running}
          aria-label="Send request"
          title="Send request"
          icon={<Play size={16} weight="fill" />}
        />
      </form>

      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        <div className="flex shrink-0 flex-col gap-2 border-b border-kumo-line p-2.5">
          <div className="flex items-center">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-kumo-subtle">
              Headers
            </span>
            <button
              type="button"
              onClick={() =>
                onRequestChange({
                  ...request,
                  headers: [...request.headers, ["", ""]]
                })
              }
              className="ml-auto flex cursor-pointer items-center gap-1 bg-transparent text-xs text-kumo-accent"
            >
              <Plus size={12} weight="bold" />
              Add header
            </button>
          </div>
          {request.headers.map(([name, value], index) => (
            <div
              // biome-ignore lint: header rows are positional
              key={index}
              className="flex items-center gap-2"
            >
              <Input
                aria-label="Header name"
                placeholder="Name"
                value={name}
                onChange={(event) => updateHeader(index, [event.target.value, value])}
                className="w-full flex-1 font-mono text-xs"
              />
              <Input
                aria-label="Header value"
                placeholder="Value"
                value={value}
                onChange={(event) => updateHeader(index, [name, event.target.value])}
                className="w-full flex-1 font-mono text-xs"
              />
              <button
                type="button"
                aria-label="Remove header"
                onClick={() =>
                  onRequestChange({
                    ...request,
                    headers: request.headers.filter(
                      (_, currentIndex) => currentIndex !== index
                    )
                  })
                }
                className="flex shrink-0 cursor-pointer items-center bg-transparent p-1 text-kumo-subtle"
              >
                <X size={13} />
              </button>
            </div>
          ))}
          {hasRequestBody ? (
            <>
              <span className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-kumo-subtle">
                Body
              </span>
              <Textarea
                aria-label="Request body"
                rows={4}
                spellCheck={false}
                value={request.body}
                onChange={(event) =>
                  onRequestChange({ ...request, body: event.target.value })
                }
                className="w-full resize-y font-mono text-xs"
              />
            </>
          ) : null}
        </div>

        <div className="flex-1 p-2.5">
          {error ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-xs text-status-error">
              {error.message}
            </pre>
          ) : result ? (
            <div className="flex flex-col gap-2 text-left">
              <div className="flex items-center gap-1.5 text-[13px] text-kumo-default">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${statusDotColor(result.response.status)}`}
                />
                {result.response.status}
              </div>
              <pre className="overflow-auto whitespace-pre-wrap break-words rounded-md bg-kumo-elevated p-2.5 font-mono text-[11px] text-kumo-subtle">
                {Object.entries(result.response.headers)
                  .map(([name, value]) => `${name}: ${value}`)
                  .join("\n")}
              </pre>
              <pre
                className={`overflow-auto whitespace-pre-wrap break-words rounded-md bg-kumo-elevated p-2.5 font-mono text-xs ${
                  result.workerError ? "text-status-error" : "text-kumo-default"
                }`}
              >
                {result.workerError
                  ? result.workerError.message
                  : prettyBody(result.response.body)}
              </pre>
            </div>
          ) : (
            <p className="text-[13px] text-kumo-subtle">
              Send a request to test your Worker&apos;s response.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export interface TestSurfaceProps {
  files: PlaygroundFiles;
  result: TestRunResult | null;
  error: RuntimeError | null;
  running: boolean;
  watch: boolean;
  inFileConcurrency: boolean;
  onWatchChange: (next: boolean) => void;
  onInFileConcurrencyChange: (next: boolean) => void;
  onRun: () => void;
  onCreate: (kind: "runtime" | "e2e") => void;
  onOpenFile: (file: string) => void;
  /** Open a recording produced by this run in the Flow recordings tab. */
  onOpenRecording?: (recordingId: string, workspaceId?: string) => void;
}

/** Test runner controls and result list; execution and file state remain external. */
export function TestSurface({
  files,
  result,
  error,
  running,
  watch,
  inFileConcurrency,
  onWatchChange,
  onInFileConcurrencyChange,
  onRun,
  onCreate,
  onOpenFile,
  onOpenRecording
}: TestSurfaceProps) {
  const testFiles = testFilesFrom(files);
  const summary = result?.summary;
  const successful = summary && summary.failed === 0;
  const recordings = result?.flowRecordings ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-kumo-line p-2">
        <Button
          variant="secondary"
          shape="square"
          loading={running}
          disabled={testFiles.length === 0}
          aria-label="Run all tests"
          title="Run all tests"
          icon={<Play size={16} weight="fill" />}
          onClick={onRun}
        />
        <ToggleIconButton
          active={watch}
          title={`Test watch: ${watch ? "on" : "off"} — rerun after edits`}
          onClick={() => onWatchChange(!watch)}
          icon={<ArrowsClockwiseIcon size={14} />}
        />
        <ToggleIconButton
          active={inFileConcurrency}
          title={`In-file parallel: ${inFileConcurrency ? "on" : "off"} — run independent tests within a file concurrently (.sequential opts out)`}
          onClick={() => onInFileConcurrencyChange(!inFileConcurrency)}
          icon={<ArrowsLeftRight size={14} />}
        />
        <div className="min-w-0 flex-1" aria-live="polite">
          <div className="flex items-center gap-1.5 text-xs font-medium text-kumo-default">
            {running ? (
              <SpinnerGap className="animate-spin" size={14} />
            ) : successful ? (
              <CheckCircle className="text-status-success" size={14} weight="fill" />
            ) : summary?.failed ? (
              <XCircle className="text-status-error" size={14} weight="fill" />
            ) : (
              <Flask size={14} />
            )}
            {running
              ? "Running in Workers runtime…"
              : summary
                ? `${summary.passed} passed${summary.failed ? `, ${summary.failed} failed` : ""}`
                : `${testFiles.length} test file${testFiles.length === 1 ? "" : "s"}`}
          </div>
          <div className="truncate text-[10px] text-kumo-subtle">
            {result
              ? `Runtime parity + production-style E2E harness · ${result.concurrency} file fork${result.concurrency === 1 ? "" : "s"}`
              : `Runtime parity + production-style E2E harness · up to ${MAX_TEST_CONCURRENCY} concurrent files`}
          </div>
        </div>
        {summary ? (
          <span className="shrink-0 font-mono text-[10px] text-kumo-subtle">
            {result.timing.totalTime}ms
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {testFiles.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="rounded-full bg-kumo-tint p-3 text-kumo-subtle">
              <Flask size={28} />
            </div>
            <div>
              <p className="text-sm font-medium text-kumo-default">Add your first test</p>
              <p className="mt-1 max-w-sm text-xs leading-5 text-kumo-subtle">
                Use unchanged Vitest + cloudflare:test files, or Wrangler&apos;s
                createTestHarness() E2E API. Workerstation runs both in real
                Workers isolates.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => onCreate("runtime")}>
                Runtime test
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onCreate("e2e")}>
                E2E test
              </Button>
            </div>
          </div>
        ) : error ? (
          <div className="p-3">
            <div className="rounded-md border border-status-error/30 bg-status-error/5 p-3">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-status-error">
                <XCircle size={14} weight="fill" /> Test run failed
              </div>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-kumo-default">
                {error.stack || error.message}
              </pre>
            </div>
          </div>
        ) : result ? (
          <div className="divide-y divide-kumo-line">
            {recordings.length > 0 ? (
              <div className="bg-kumo-tint/40 p-3">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-kumo-default">
                  <FilmStrip size={14} weight="fill" />
                  {recordings.length} flow recording
                  {recordings.length === 1 ? "" : "s"} from this run
                </div>
                <div className="flex flex-col gap-1">
                  {recordings.map((recording) => (
                    <button
                      key={recording.recordingId}
                      type="button"
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-kumo-line bg-kumo-base px-2 py-1.5 text-left hover:bg-kumo-tint disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={!onOpenRecording}
                      onClick={() =>
                        onOpenRecording?.(
                          recording.recordingId,
                          recording.workspaceId
                        )
                      }
                      aria-label={`Open recording ${recording.recordingId} in the Flow recordings tab`}
                      title={`Open recording ${recording.recordingId} in the Flow recordings tab`}
                    >
                      <FilmStrip size={13} className="shrink-0 text-kumo-subtle" />
                      <span className="min-w-0 flex-1 truncate text-xs text-kumo-default">
                        {recording.flowName}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] uppercase text-kumo-subtle">
                        {recording.mode} · {recording.state}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {result.fatal ? (
              <div className="bg-status-error/5 p-3">
                <div className="text-xs font-semibold text-status-error">
                  {result.fatal.name}: {result.fatal.message}
                </div>
                {result.fatal.stack ? (
                  <pre className="mt-2 overflow-auto whitespace-pre-wrap font-mono text-[10px] text-kumo-subtle">
                    {result.fatal.stack}
                  </pre>
                ) : null}
              </div>
            ) : null}
            {result.tests.map((test, index) => (
              <div key={`${test.file}:${test.fullName}:${index}`} className="px-2 py-2">
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-start gap-2 bg-transparent text-left"
                  onClick={() => onOpenFile(test.file)}
                  title={`Open ${test.file}`}
                >
                  {test.status === "passed" ? (
                    <CheckCircle
                      className="mt-0.5 shrink-0 text-status-success"
                      size={14}
                      weight="fill"
                    />
                  ) : test.status === "failed" ? (
                    <XCircle
                      className="mt-0.5 shrink-0 text-status-error"
                      size={14}
                      weight="fill"
                    />
                  ) : (
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-kumo-line" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs text-kumo-default">
                      {test.fullName}
                    </span>
                    <span className="block truncate font-mono text-[10px] text-kumo-subtle">
                      {test.file}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-kumo-subtle">
                    {test.duration}ms
                  </span>
                </button>
                {test.error ? (
                  <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-kumo-tint p-2 font-mono text-[10px] leading-4 text-status-error">
                    {test.error.stack || `${test.error.name}: ${test.error.message}`}
                  </pre>
                ) : null}
                {test.logs.length > 0 ? (
                  <details className="mt-2 pl-5 text-[10px] text-kumo-subtle">
                    <summary className="cursor-pointer select-none">
                      {test.logs.length} console message{test.logs.length === 1 ? "" : "s"}
                    </summary>
                    <div className="mt-1 space-y-1 font-mono">
                      {test.logs.map((log, logIndex) => (
                        <div key={`${log.timestamp}:${logIndex}`}>{log.message}</div>
                      ))}
                    </div>
                  </details>
                ) : null}
              </div>
            ))}
            {result.tests.length === 0 && !result.fatal ? (
              <div className="p-6 text-center text-xs text-kumo-subtle">
                No tests were registered by the selected files.
              </div>
            ) : null}
            <details className="p-3 text-[10px] text-kumo-subtle">
              <summary className="cursor-pointer select-none">Build details</summary>
              <div className="mt-2 space-y-1 font-mono">
                <div>build {result.timing.buildTime}ms · run {result.timing.runTime}ms</div>
                {result.bundleInfo.warnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </div>
            </details>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-kumo-subtle">
            <Flask size={30} />
            <p className="text-sm text-kumo-default">Ready to test</p>
            <p className="max-w-sm text-xs leading-5">
              Run {testFiles.length} file{testFiles.length === 1 ? "" : "s"} with
              per-run storage isolation and unchanged local test imports.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export type ConsoleSource = "worker" | "test";

export interface ConsoleSurfaceProps {
  source: ConsoleSource;
  result: RunResult | null;
  error: RuntimeError | null;
  testResult: TestRunResult | null;
  testError: RuntimeError | null;
  /** Optional run identity shown beside Console when the host has one. */
  contextLabel?: string;
}

/** Runtime output for either the worker request or the selected test run. */
export function ConsoleSurface({
  source,
  result,
  error,
  testResult,
  testError,
  contextLabel
}: ConsoleSurfaceProps) {
  const isTest = source === "test";
  const activeTestResult = isTest ? testResult : null;
  const activeTestError = isTest ? testError : null;
  const activeResult = isTest ? null : result;
  const activeError = isTest ? null : error;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-kumo-line px-3 py-2">
        <span className="text-[13px] font-semibold text-kumo-default">Console</span>
        <div className="flex min-w-0 items-center gap-2">
          {contextLabel ? (
            <span className="truncate text-[11px] text-kumo-subtle">{contextLabel}</span>
          ) : null}
          {isTest
            ? activeTestResult && (
                <span className="font-mono text-[11px] text-kumo-subtle">
                  build {activeTestResult.timing.buildTime}ms · run {activeTestResult.timing.runTime}ms · total {activeTestResult.timing.totalTime}ms
                </span>
              )
            : activeResult && (
                <span className="font-mono text-[11px] text-kumo-subtle">
                  build {activeResult.timing.buildTime}ms · load {activeResult.timing.loadTime}ms · run {activeResult.timing.runTime}ms · total {activeResult.timing.totalTime}ms
                </span>
              )}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3 font-mono text-xs">
        {isTest ? (
          <>
            {activeTestError ? (
              <pre className="whitespace-pre-wrap break-words text-status-error">
                {activeTestError.stack || activeTestError.message}
              </pre>
            ) : activeTestResult?.logs.length ? (
              activeTestResult.logs.map((log, index) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className={`flex gap-1.5 ${consoleColor(log.level)}`}
                >
                  <span className="opacity-50">{consolePrefix(log.level)}</span>
                  <span className="whitespace-pre-wrap break-words">{log.message}</span>
                </div>
              ))
            ) : (
              <span className="text-kumo-subtle">
                Test console output appears here and alongside its test.
              </span>
            )}
          </>
        ) : (
          <>
            {activeError ? (
              <>
                <pre className="whitespace-pre-wrap break-words text-status-error">
                  {activeError.message}
                </pre>
                {activeError.stack ? (
                  <pre className="mt-2 whitespace-pre-wrap break-words text-kumo-subtle">
                    {activeError.stack}
                  </pre>
                ) : null}
              </>
            ) : null}

            {activeResult?.workerError ? (
              <>
                <pre className="whitespace-pre-wrap break-words text-status-error">
                  {activeResult.workerError.message}
                </pre>
                {activeResult.workerError.stack ? (
                  <pre className="mt-2 whitespace-pre-wrap break-words text-kumo-subtle">
                    {activeResult.workerError.stack}
                  </pre>
                ) : null}
              </>
            ) : null}

            {activeResult?.logs.length
              ? activeResult.logs.map((log, index) => (
                  <div
                    key={`${log.timestamp}-${index}`}
                    className={`flex gap-1.5 ${consoleColor(log.level)}`}
                  >
                    <span className="opacity-50">{consolePrefix(log.level)}</span>
                    <span className="whitespace-pre-wrap break-words">{log.message}</span>
                  </div>
                ))
              : !activeError && !activeResult?.workerError ? (
                  <span className="text-kumo-subtle">
                    No console output. Use <code>console.log()</code> in your
                    worker to see logs here.
                  </span>
                ) : null}

            {activeResult?.bundleInfo.warnings.length ? (
              <pre className="mt-2 whitespace-pre-wrap break-words text-status-warning">
                {activeResult.bundleInfo.warnings.join("\n")}
              </pre>
            ) : null}

            {activeResult ? (
              <p className="mt-3 border-t border-kumo-line pt-2 text-[11px] text-kumo-subtle">
                main: {activeResult.bundleInfo.mainModule} · modules (
                {activeResult.bundleInfo.modules.length}): {" "}
                {activeResult.bundleInfo.modules.join(", ")}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/** A discriminated surface description useful to recursive layout hosts. */
export type RuntimeToolSurface =
  | { kind: "preview"; props: PreviewSurfaceProps }
  | { kind: "http"; props: HttpSurfaceProps }
  | { kind: "test"; props: TestSurfaceProps }
  | { kind: "console"; props: ConsoleSurfaceProps };

/** Render a runtime tool surface without owning any application state. */
export function RuntimeToolSurfaceView({
  surface
}: {
  surface: RuntimeToolSurface;
}): ReactNode {
  switch (surface.kind) {
    case "preview":
      return <PreviewSurface {...surface.props} />;
    case "http":
      return <HttpSurface {...surface.props} />;
    case "test":
      return <TestSurface {...surface.props} />;
    case "console":
      return <ConsoleSurface {...surface.props} />;
  }
}
